import React, { useCallback } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { ToggleButtonOption, ToggleButtonsProps } from './types';

export const ToggleButtons: React.FC<ToggleButtonsProps> = ({
	options,
	selectedOption,
	onChange,
	label,
	isCompact,
	expDesignRefresh,
}) => {
	const getButtonWithContext = useCallback(
		({ option, isSelected }: { isSelected: boolean; option: ToggleButtonOption }) => (
			<Pressable
				key={option.id}
				role="radio"
				aria-checked={isSelected}
				xcss={[
					expDesignRefresh ? baseStylesDesignRefresh : baseStyles,
					!isCompact && (expDesignRefresh ? wideStylesDesignRefresh : wideStyles),
					isSelected && (expDesignRefresh ? selectedStylesDesignRefresh : selectedStyles),
				]}
				onClick={(event, analyticsEvent) =>
					!isSelected && onChange(option.id, event, analyticsEvent)
				}
				isDisabled={option.isDisabled}
				testId={option.testId}
				interactionName={option.interactionName}
			>
				{option.label}
			</Pressable>
		),
		[onChange, isCompact, expDesignRefresh],
	);

	return (
		<Box
			// eslint-disable-next-line @atlassian/a11y/no-noninteractive-element-to-interactive-role
			role="radiogroup"
			xcss={expDesignRefresh ? containerStylesDesignRefresh : containerStyles}
			aria-label={label}
			as="fieldset"
		>
			{options.map((option) =>
				getButtonWithContext({
					option,
					isSelected: option.id === selectedOption,
				}),
			)}
		</Box>
	);
};

const containerStyles = xcss({
	borderColor: 'color.border',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderRadius: token('radius.small'),
	paddingInline: 'space.025',
	gap: 'space.025',
	display: 'inline-flex',
	alignItems: 'center',
	height: '2rem',
});

const containerStylesDesignRefresh = xcss({
	borderColor: 'color.border',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderRadius: token('radius.small', '6px'),
	padding: 'space.050',
	gap: 'space.025',
	display: 'inline-flex',
	alignItems: 'center',
	height: '2rem',
});

const baseStyles = xcss({
	background: token('color.background.neutral.subtle'),
	border: `${token('border.width')} solid transparent`,
	borderRadius: token('radius.xsmall'),
	color: 'color.text.subtle',
	height: '26px',
	fontWeight: 'font.weight.medium',
	padding: 'space.0',
	':hover': {
		background: token('color.background.neutral.subtle.hovered'),
		color: 'color.text.subtle',
	},
	':active': {
		background: token('color.background.neutral.subtle.pressed'),
		color: 'color.text.subtle',
	},
	':focus': {
		outlineOffset: 'space.0',
	},
	':disabled': {
		background: token('color.background.disabled'),
		color: 'color.text.disabled',
	},
});

const baseStylesDesignRefresh = xcss({
	background: token('color.background.neutral.subtle'),
	border: `${token('border.width')} solid transparent`,
	borderRadius: token('radius.xsmall', '3px'),
	color: 'color.text.subtle',
	height: '24px',
	fontWeight: 'font.weight.medium',
	padding: 'space.0',
	':hover': {
		background: token('color.background.neutral.subtle.hovered'),
		color: 'color.text.subtle',
	},
	':active': {
		background: token('color.background.neutral.subtle.pressed'),
		color: 'color.text.subtle',
	},
	':focus': {
		outlineOffset: 'space.0',
	},
	':disabled': {
		background: token('color.background.disabled'),
		color: 'color.text.disabled',
	},
});

const selectedStyles = xcss({
	background: token('color.background.selected'),
	border: `${token('border.width')} solid ${token('color.border.selected')}`,
	color: 'color.text.selected',
	cursor: 'default',
	':hover': {
		background: token('color.background.selected'),
		color: 'color.text.selected',
	},
});

const selectedStylesDesignRefresh = xcss({
	background: token('color.background.selected'),
	color: 'color.text.selected',
	cursor: 'default',
	':hover': {
		background: token('color.background.selected'),
		color: 'color.text.selected',
	},
});

const wideStyles = xcss({
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
});

const wideStylesDesignRefresh = xcss({
	paddingLeft: 'space.075',
	paddingRight: 'space.100',
});
